/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 8 de mayo de 2025
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */

import api from "../axiosConfig";
import dayjs from "dayjs";

const STORAGE_KEY = "preop_last_answered";
const isBrowser = typeof window !== "undefined";

// ============================
// 📡 API Calls
// ============================

export const fetchPreOperaciones = (conductorId, vehiculoId) =>
  api
    .get("/conductor/pre-operaciones", { params: { conductorId, vehiculoId } })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error al obtener preoperaciones");
      throw err;
    });

export const generatePreOperacionPDF = (payload) =>
  api
    .post("/conductor/pre-operaciones/generate-pdf", payload, {
      responseType: "blob",
      timeout: 30000,
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res.status !== 200) {
        throw new Error(`Error en la respuesta del servidor: ${res.status}`);
      }
      return res.data;
    })
    .catch((err) => {
      console.error("Error al generar PDF de preoperación");

      // Si ya fue completado (409), propagar con status para que el componente lo maneje
      if (err.response?.status === 409) {
        const error409 = new Error("El formulario preoperacional ya fue completado hoy.");
        error409.response = { status: 409, data: { already_completed: true } };
        throw error409;
      }

      // Manejo de errores de respuesta del servidor (blob)
      if (err.response?.data) {
        if (typeof err.response.data === "object" && !(err.response.data instanceof Blob)) {
          throw err;
        }

        if (err.response.data instanceof Blob) {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const errorData = JSON.parse(reader.result);
                reject(new Error(errorData.message || "Error al generar PDF"));
              } catch (e) {
                reject(new Error("Error al generar PDF"));
              }
            };
            reader.onerror = () => reject(err);
            reader.readAsText(err.response.data);
          });
        }
      }
      throw err;
    });

export const fetchAnsweredToday = async (conductorId, vehiculoId) => {
  if (!conductorId || !vehiculoId) {
    return false;
  }

  try {
    const today = dayjs().format("YYYY-MM-DD");

    const { data } = await api.get("/conductor/pre-operaciones/respuestas", {
      params: { conductorId, vehiculoId, fecha: today },
    });

    const hasAnswered = Array.isArray(data) && data.length > 0;

    if (hasAnswered) {
      setAnsweredToday(conductorId);
    }

    return hasAnswered;
  } catch (error) {
    return hasAnsweredToday(conductorId);
  }
};

// ============================
// 💾 LocalStorage Utilities
// ============================

export const hasAnsweredToday = (userId) => {
  if (!isBrowser) return false;
  const map = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  return map[userId] === dayjs().format("YYYY-MM-DD");
};

export const setAnsweredToday = (userId) => {
  if (!isBrowser) return;
  const map = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  map[userId] = dayjs().format("YYYY-MM-DD");
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
};

export const clearAnsweredToday = (userId) => {
  if (!isBrowser) return;
  const map = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  if (map[userId]) {
    delete map[userId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  }
};
